import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { getApiBaseUrl } from '../utils/api';

export function ConfirmSpot() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [playerName, setPlayerName] = useState('');
  
  const sessionId = searchParams.get('session');
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (sessionId && token) {
      confirmSpot();
    } else {
      setStatus('error');
      setMessage('Invalid confirmation link');
    }
  }, [sessionId, token]);
  
  const confirmSpot = async () => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${sessionId}/confirm-spot/${token}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus('success');
        setMessage(data.message);
        setPlayerName(data.player_name);
      } else {
        if (data.detail?.includes('expired')) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
        setMessage(data.detail || 'Failed to confirm spot');
      }
    } catch (error) {
      console.error('Confirmation error:', error);
      setStatus('error');
      setMessage('Something went wrong. Please try again or contact support.');
    }
  };
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <img 
          src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
          alt="STAY ON Logo" 
          className="h-12 mx-auto mb-8"
        />
        
        {status === 'loading' && (
          <div className="bg-card border border-border rounded-xl p-8">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <h2 className="text-xl font-heading text-white mb-2">Confirming Your Spot...</h2>
            <p className="text-muted-foreground">Please wait a moment</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="bg-card border border-green-500/30 rounded-xl p-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} weight="fill" className="text-green-500" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">You're In!</h2>
            <p className="text-muted-foreground mb-6">
              {playerName ? `${playerName} is now registered for the session.` : message}
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full btn-primary"
              >
                Go to Dashboard
              </Button>
              <Button 
                onClick={() => navigate('/sessions')}
                variant="outline"
                className="w-full"
              >
                View All Sessions
              </Button>
            </div>
          </div>
        )}
        
        {status === 'expired' && (
          <div className="bg-card border border-yellow-500/30 rounded-xl p-8">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock size={48} weight="fill" className="text-yellow-500" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">Link Expired</h2>
            <p className="text-muted-foreground mb-6">
              This confirmation link has expired. The spot may have been offered to someone else.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Contact <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline">info@stayonhoops.com</a> if you have questions.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="w-full btn-primary"
            >
              Go to Homepage
            </Button>
          </div>
        )}
        
        {status === 'error' && (
          <div className="bg-card border border-red-500/30 rounded-xl p-8">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={48} weight="fill" className="text-red-500" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <p className="text-sm text-muted-foreground mb-6">
              Please contact <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline">info@stayonhoops.com</a> for assistance.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="w-full btn-primary"
            >
              Go to Homepage
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
